import React, {createContext, useCallback, useContext, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import HighlightedInputMedia from '../components/fileLoader/input.media';
import useImgFileReader from '../components/fileLoader/util/useImgFileReader';
import CalculatorSelectionModal from '../components/slides/highlighted/calculator/CalculatorSelectionModal';
import {MAX_NUMBER_OF_ANSWERS} from '../constants';
import {ELEMENT} from '../incase-domain/elements/base/ElementTypes';
import {IElementImage} from '../incase-domain/elements/image';
import {IElementPhotoAnswerQuestion} from '../incase-domain/elements/question/photoAnswerQuestion';
import {ElementProps} from '../service/element';
import {answersCreator} from '../service/utils';
import CreateVideoElementModal from '../ui/components/common/modal/createdElementModals/CreateVideoElementModal';
import QuestionReportModal from '../ui/components/common/modal/createdElementModals/QuestionReportModal';
import CreateModalElementModal from '../ui/components/common/modal/CreateModalElementModal';
import useElementActions from '../utils/hooks/slideUpdates/useElementActions';
import ChatbotModal from '../components/slides/highlighted/chatbot/ChatbotModal';
import {ElementPlacement} from '../utils/elementDragDrop';
import {selectedPresentationSelector} from '../store/selectors/presentation.selectors';
import {isWordCloudSource, wordCloudReportLayout} from '../components/slides/highlighted/questionReport/utils';

type AddElementsContextValue = {
    handleElementAction: <T extends ELEMENT>(type: T, newElementProps?: ElementProps<T>, placement?: ElementPlacement | null) => void;
};

const AddElementsContext = createContext<AddElementsContextValue | null>(null);

function AddElementsContextProvider({children}: {children: JSX.Element}): JSX.Element {
    const imgInputRef = useRef<HTMLInputElement>(null);
    const imgMultipleInputRef = useRef<HTMLInputElement>(null);
    const pendingPlacementRef = useRef<ElementPlacement | null>(null);
    const {addNewElement} = useElementActions();
    const presentation = useSelector(selectedPresentationSelector);

    const [isElementModalOpen, setIsElementModalOpen] = useState(false);
    const [lastElementType, setLastElementType] = useState<ELEMENT>(ELEMENT.STATIC_TEXT);

    const addWithPendingPlacement = useCallback(
        <T extends ELEMENT>(type: T, props?: ElementProps<T>): void => {
            const placement = pendingPlacementRef.current;
            pendingPlacementRef.current = null;
            const mergedProps = placement ? ({...props, ...placement} as ElementProps<T>) : props;
            addNewElement(type, mergedProps);
        },
        [addNewElement]
    );

    const clearPendingPlacement = useCallback(() => {
        pendingPlacementRef.current = null;
    }, []);

    const handleModalClose = useCallback(() => {
        setIsElementModalOpen(false);
        clearPendingPlacement();
    }, [clearPendingPlacement]);

    const onFileLoad = useImgFileReader((imgs) => {
        const images = imgs.slice(0, MAX_NUMBER_OF_ANSWERS);
        if (lastElementType === ELEMENT.IMAGE_GALLERY) {
            addWithPendingPlacement(lastElementType, {images} as {images: IElementImage[]});
            return;
        }

        if (lastElementType === ELEMENT.PHOTO_ANSWER_QUESTION) {
            const answers = answersCreator(images.length).map((answer, index) => ({
                ...answer,
                imgSrc: images[index].imageSrc,
                imageRatio: images[index].widthHeightRatio,
            })) as IElementPhotoAnswerQuestion['answers'];

            addWithPendingPlacement(lastElementType, {answers});
            return;
        }

        if (lastElementType === ELEMENT.IMAGE) {
            const {imageSrc, height, widthHeightRatio, width} = images[0];
            addWithPendingPlacement(lastElementType, {imageSrc, width, height, widthHeightRatio});
        }

        if (lastElementType === ELEMENT.IMAGE_MARKUP) {
            const {imageSrc, widthHeightRatio} = images[0];
            const maxHeight = 45;
            let width = 40;
            const height = width / widthHeightRatio;

            if (height > maxHeight) {
                width = maxHeight * widthHeightRatio;
            }

            addWithPendingPlacement(lastElementType, {imageSrc, width: `${width}%`, widthHeightRatio, markedAreas: []});
        }
    });

    const handleElementAction = useCallback(
        <T extends ELEMENT>(type: T, elementProps?: ElementProps<T>, placement?: ElementPlacement | null): void => {
            if (placement) {
                pendingPlacementRef.current = placement;
            } else {
                pendingPlacementRef.current = null;
            }

            setLastElementType(type);
            switch (type) {
                case ELEMENT.QUESTION_REPORT:
                    setIsElementModalOpen(true);
                    break;
                case ELEMENT.IMAGE_GALLERY:
                case ELEMENT.PHOTO_ANSWER_QUESTION:
                    imgMultipleInputRef.current?.click();
                    break;
                case ELEMENT.IMAGE:
                case ELEMENT.IMAGE_MARKUP:
                    imgInputRef.current?.click();
                    break;
                case ELEMENT.VIDEO:
                case ELEMENT.CALCULATOR:
                case ELEMENT.CHATBOT:
                case ELEMENT.MODAL:
                    setIsElementModalOpen(true);
                    break;
                default:
                    addWithPendingPlacement(type, elementProps);
            }
        },
        [addWithPendingPlacement]
    );

    const ctxValue = useMemo(() => ({handleElementAction}), [handleElementAction]);

    return (
        <AddElementsContext.Provider value={ctxValue}>
            {children}

            <CalculatorSelectionModal
                isOpen={isElementModalOpen && lastElementType === ELEMENT.CALCULATOR}
                onConfirm={(calculatorId) => {
                    addWithPendingPlacement(ELEMENT.CALCULATOR, {calculatorId});
                    setIsElementModalOpen(false);
                }}
                onClose={handleModalClose}
            />

            <ChatbotModal
                isOpen={isElementModalOpen && lastElementType === ELEMENT.CHATBOT}
                onConfirm={(chatbotProps) => {
                    addWithPendingPlacement(ELEMENT.CHATBOT, chatbotProps);
                    setIsElementModalOpen(false);
                }}
                onClose={handleModalClose}
            />

            <CreateVideoElementModal
                isOpen={isElementModalOpen && lastElementType === ELEMENT.VIDEO}
                onConfirm={(videoSource, widthHeightRatio) => {
                    addWithPendingPlacement(ELEMENT.VIDEO, {
                        videoSource,
                        ...(widthHeightRatio ? {widthHeightRatio, height: 'auto'} : {}),
                    });
                    setIsElementModalOpen(false);
                }}
                onClose={handleModalClose}
            />

            <CreateModalElementModal
                isOpen={isElementModalOpen && lastElementType === ELEMENT.MODAL}
                onConfirm={(modalData) => {
                    addWithPendingPlacement(ELEMENT.MODAL, modalData);
                    setIsElementModalOpen(false);
                }}
                onClose={handleModalClose}
            />

            <QuestionReportModal
                isOpen={isElementModalOpen && lastElementType === ELEMENT.QUESTION_REPORT}
                onConfirm={(questionElementId, graphSize, graphType, slideName) => {
                    const source = presentation?.slides
                        ?.flatMap((slide) => slide.elements)
                        .find((element) => element.id === questionElementId);
                    addWithPendingPlacement(ELEMENT.QUESTION_REPORT, {
                        questionElementId,
                        graphSize,
                        graphType,
                        slideName,
                        ...(isWordCloudSource(source) ? wordCloudReportLayout() : {}),
                    });
                    setIsElementModalOpen(false);
                }}
                onClose={handleModalClose}
            />

            <div className="u-d-none">
                <HighlightedInputMedia inputRef={imgInputRef} onLoad={onFileLoad} type="image" />
                <HighlightedInputMedia multipleFiles inputRef={imgMultipleInputRef} onLoad={onFileLoad} type="image" />
            </div>
        </AddElementsContext.Provider>
    );
}

export function useAddElementAction(): AddElementsContextValue['handleElementAction'] {
    const ctx = useContext(AddElementsContext);
    if (!ctx) {
        throw new Error('useAddElementAction must be used within AddElementsContextProvider');
    }
    return ctx.handleElementAction;
}

export default AddElementsContextProvider;
